import { Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { LABELS } from "@match/shared";
import { PairView } from "@/components/pair";
import { BB_LEG_LABELS, isBrokerPair, ratioText } from "@/components/broker-pair";
import { ConvertedAmount, Progress } from "@/components/session";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, Dash, DecimalField, ErrorNote, Field, Hint, MoreMenu, inputClass } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { dateTime, lots, money, points, sentence, signClass } from "@/lib/format";
import { decimalInput, decimalOrZero, parseDecimal } from "@/lib/units";
import { keys, useDebounced, useMirrorIsFake, useOpenRiskEvents, useRefreshDirectory, useServers, useSessionSymbols } from "@/lib/queries";
import { SymbolPanel, usePeersOf } from "@/components/symbols";
import { EventCard } from "@/pages/Events";
import type { AccountHistoryRow, AccountHistoryTotal, Candles, HedgeLeg, HedgePair, LegCommission, LegPlan, LevelsReading, Preview, PreviewSpec, SessionDetail, ValueInUse, ValuesInUse } from "@/lib/types";

/*
 * Operatività di una Fase: la coppia di conti, i parametri dell'operazione, il
 * riepilogo, l'apertura, la coppia aperta e la sua cronistoria. Una sola
 * implementazione, usata dalla postazione di Operazione e dalla scheda
 * Operazione del dettaglio.
 *
 * L'operatore scrive DENARO — «rischio 200» — e il backend ne ricava i punti.
 * Quello che si legge accanto ai campi non è mai il numero digitato: è il
 * rischio VERO dopo l'arrotondamento dei punti e dei lotti, e sono due numeri,
 * uno per gamba. 200 su 1,33 lotti fanno 150 punti, cioè 199,50: si mostra
 * 199,50, e sul broker un numero diverso ancora.
 */

/**
 * Il grafico si carica SOLO quando serve, cioe' su una coppia aperta: sono ~45 kB
 * gzip di libreria, e questa e' la pagina che si apre tutti i giorni anche
 * quando non c'e' niente a mercato.
 */
const LevelsChart = lazy(() => import("@/components/chart").then((m) => ({ default: m.LevelsChart })));

const OPEN_STATES = ["PENDING", "BOTH_OPEN", "ONE_LEG"];

/** Le coppie a mercato adesso, in una fase: di solito zero o una. */
const openPairsOf = (s: SessionDetail) => s.pairs.filter((p) => OPEN_STATES.includes(p.state));

/** Le coppie chiuse (o abbandonate) della fase, dalla più recente. */
const closedPairsOf = (s: SessionDetail) =>
  s.pairs.filter((p) => !OPEN_STATES.includes(p.state)).sort((a, b) => (a.openedAt < b.openedAt ? 1 : a.openedAt > b.openedAt ? -1 : 0));

/** Larghezza dello schermo sopra la quale i due grafici stanno affiancati. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** Gli eventi aperti di una fase. Niente riquadro quando non ce n'e' nessuno. */
function SessionEvents({ sessionId }: { sessionId: string }) {
  const q = useOpenRiskEvents({ sessionId });
  if (!q.data?.rows.length) return null;
  return (
    <div className="space-y-2">
      {q.data.rows.map((e) => (
        <EventCard key={e.id} e={e} compact />
      ))}
    </div>
  );
}

/**
 * Quando si addebita la commissione, detto solo quando e' UNA cosa sola.
 * Con l'addebito diviso non si scrive niente: il totale e' gia' li', e
 * «meta' e meta'» in una riga di frizione non aiuta nessuno.
 */
function chargeLabel(c: LegCommission): string {
  if (c.total.amount <= 0) return "";
  if (c.atClose.amount === 0) return " all'apertura";
  if (c.atOpen.amount === 0) return " alla chiusura";
  return "";
}

export function SessionTrade({ s, canWrite }: { s: SessionDetail; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const isFake = useMirrorIsFake();
  const [direction, setDirection] = useState<"LONG" | "SHORT">("LONG");
  // I valori dell'ultima operazione sono i PREDEFINITI, non configurazione:
  // senza operazioni precedenti i campi restano vuoti (DECISIONE 18).
  const last = s.lastOperation;
  const [propLots, setPropLots] = useState(last ? String(Number(last.propLots)) : "");
  const [multiplier, setMultiplier] = useState(last ? String(Number(last.brokerMultiplier)) : "");
  const [slMoney, setSlMoney] = useState(last?.slMoney ? String(Number(last.slMoney)) : "");
  const [tpMoney, setTpMoney] = useState(last?.tpMoney ? String(Number(last.tpMoney)) : "");
  // Il simbolo si sceglie a ogni operazione. `null` = non toccato: vale il
  // predefinito della fase, ma solo se e' utilizzabile (vedi `symbol`).
  const [chosenSymbol, setChosenSymbol] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Il `code` del rifiuto: non tutti i «non riuscito» chiedono la stessa cosa. */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  /** Secondo clic richiesto sull'operazione che può bruciare il conto. */
  const [burnConfirm, setBurnConfirm] = useState(false);
  const symbols = useSessionSymbols(s.id);

  const openPairs = openPairsOf(s);
  const hasOpen = openPairs.length > 0;
  const closedPairs = closedPairsOf(s);
  // Di solito zero o una (una seconda non si apre finche' questa e' a mercato):
  // e' la coppia il cui SL/TP si legge e si scrive dal grafico sopra le schede.
  const mainPair = openPairs[0] ?? null;
  const levelsInput = useLevelsInput(mainPair);
  const propCur = s.propAccount.currency;
  /*
   * BROKER CONTRO BROKER: le gambe sono il broker A e il broker B, e il
   * moltiplicatore e' il rapporto di copertura della Fase, in sola lettura (il
   * backend rifiuta un numero diverso). Niente anti-hedging, DD ne' bruciatura.
   */
  const bb = isBrokerPair(s);
  const legA = bb ? "broker A" : "prop";
  // Lotto, moltiplicatore e stop loss servono al piano: senza, niente da mostrare.
  const op = { lots: decimalInput(propLots), mult: bb ? s.hedgeRatio : decimalInput(multiplier), sl: decimalInput(slMoney), tp: decimalInput(tpMoney) };
  // Un campo scritto ma non numerico non e' un campo vuoto: lo dice il campo, e qui il motivo del blocco.
  const fieldError = [propLots, ...(bb ? [] : [multiplier]), slMoney, tpMoney].some((t) => parseDecimal(t).kind === "error");
  const complete = !!op.lots && !!op.mult && !!op.sl && !fieldError;
  // Un predefinito bloccato o non piu' mappato NON si preseleziona: il menu
  // chiede di scegliere, invece di proporre cio' che l'apertura rifiuterebbe.
  const symbolList = symbols.data?.symbols ?? [];
  /** «Mappa adesso»: il server e il simbolo da mappare, aperti nel pannello della pagina Server. */
  const [mapNow, setMapNow] = useState<{ serverId: string; canonical: string } | null>(null);
  const servers = useServers();
  const peersOf = usePeersOf();
  const mapNowServer = mapNow ? (servers.data ?? []).find((x) => x.id === mapNow.serverId) : undefined;
  /*
   * ARRIVATI DA «MAPPA ADESSO» DELL'AVVISO (`?mappa=1`): il pannello si apre
   * da solo sul simbolo della Fase e sul primo server che non lo ha. Una volta
   * sola — il parametro si toglie dall'indirizzo — e solo per chi puo' scrivere.
   */
  const [params, setParams] = useSearchParams();
  const chiedeMappa = params.get("mappa") === "1";
  useEffect(() => {
    if (!chiedeMappa || !symbols.data || !servers.data) return;
    const voce = symbols.data.incomplete.find((i) => i.canonicalSymbol === s.canonicalSymbol) ?? symbols.data.incomplete[0];
    const manca = voce?.missing[0];
    if (canWrite && voce && manca) setMapNow({ serverId: manca.serverId, canonical: voce.canonicalSymbol });
    const p = new URLSearchParams(params);
    p.delete("mappa");
    setParams(p, { replace: true });
  }, [chiedeMappa, canWrite, symbols.data, servers.data, s.canonicalSymbol, params, setParams]);
  const wantedSymbol = chosenSymbol ?? (symbols.data?.defaultIsUsable ? symbols.data.defaultSymbol : null);
  const symbolEntry = symbolList.find((x) => x.canonicalSymbol === wantedSymbol) ?? null;
  const symbol = symbolEntry?.canonicalSymbol ?? "";
  const opKey = JSON.stringify({ ...op, symbol });
  // Una preview per tasto premuto sarebbe una richiesta per tasto premuto.
  const debounced = useDebounced(complete && !!symbol && !hasOpen ? opKey : null, 300);
  const preview = useQuery({
    // Sotto "sessions": ogni modifica a conti o fasi la ricalcola.
    queryKey: [...keys.sessions, "preview", s.id, direction, s.state, debounced, s.summary.executedOps, s.propAccount.isConnected, s.brokerAccount?.isConnected],
    queryFn: () => {
      const o = JSON.parse(debounced!) as typeof op & { symbol: string };
      return api.sessions.preview(s.id, { direction, lots: o.lots!, multiplier: o.mult!, canonicalSymbol: o.symbol, slMoney: o.sl!, ...(o.tp ? { tpMoney: o.tp } : {}) });
    },
    // Conto scollegato: nessuna anteprima (il backend non collega), la pagina dice «premi Connetti».
    enabled: !!debounced && s.propAccount.isConnected && !!s.brokerAccount?.isConnected,
    placeholderData: (prev) => prev,
  });

  const run = (name: string, fn: () => Promise<unknown>) => async () => {
    setBusy(name);
    setError(null);
    setErrorCode(null);
    setNotice(null);
    setWarnings([]);
    try {
      const r = await fn();
      if (r && typeof r === "object" && "warnings" in r) setWarnings((r as { warnings: string[] }).warnings);
      if (r && typeof r === "object" && "positions" in r) {
        const b = r as Awaited<ReturnType<typeof api.dev.mirrorBootstrap>>;
        setNotice(`Mirror finto: ${b.accounts} conti collegati, ${b.positions} posizioni rimesse a mercato.${b.excluded.map((x) => ` Escluso ${x.label} (${x.mt5Login}): ${x.reason}`).join("")}`);
      }
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  /*
   * IL MOTIVO CHE ARRIVA DALL'ANTEPRIMA, MA SOLO SE E' ANCORA VERO.
   *
   * L'anteprima e il dettaglio della fase sono due richieste diverse. La prima
   * non gira mentre c'e' una coppia a mercato (`enabled`) e tiene l'ultima
   * risposta (`placeholderData`, e la cache della stessa chiave): quando la
   * coppia si chiude, per il tempo di una nuova anteprima resta a schermo
   * «C'è già una coppia aperta» — detto dal server quando la coppia c'era —
   * mentre il dettaglio, che e' gia' aggiornato, dice «Niente a mercato». Qui
   * `hasOpen` e' falso: un motivo sulle coppie aperte, letto da una risposta
   * che si sta ancora rifacendo, non si mostra.
   */
  // Anti-hedging: si vede prima di premere, e si rifa' a ogni cambio di simbolo o direzione. Solo database.
  const hedging = useQuery({
    queryKey: [...keys.sessions, "hedging", s.propAccount.id, symbol, direction, s.summary.executedOps],
    queryFn: () => api.singleTrades.hedgingCheck(s.propAccount.id, { canonicalSymbol: symbol, side: direction === "LONG" ? "BUY" : "SELL" }),
    enabled: !!symbol && !hasOpen && !bb,
  });
  const hedgingMessage = hedging.data?.blocker?.message ?? null;
  const rawPreviewBlocker = preview.data?.openBlocker ?? null;
  const aboutOpenPairs = rawPreviewBlocker?.code === "PAIR_ALREADY_OPEN" || rawPreviewBlocker?.code === "PAIR_LEVELS_DESYNC";
  const staleOpenBlocker = aboutOpenPairs && !hasOpen && (preview.isFetching || preview.isPlaceholderData || debounced !== opKey);
  const previewBlocker = staleOpenBlocker ? null : rawPreviewBlocker;

  // Lo scarto fra le gambe blocca un'apertura nuova se supera la soglia SU
  // ALMENO UNA delle coppie a mercato: di solito ce n'e' una sola.
  const anyDriftBlocks = openPairs.some((p) => {
    const lv = s.levels?.[p.id];
    return lv?.measured && lv.driftMoney && (lv.driftMoney.amount === null || Math.abs(lv.driftMoney.amount) >= s.levelsDriftLimit);
  });

  // Il motivo scritto per cui non si apre. Con una coppia a mercato lo si sa
  // già; altrimenti lo dice il backend, con la stessa regola della rotta.
  const scollegato = !s.propAccount.isConnected
    ? bb
      ? "Conto broker A scollegato: premi Connetti"
      : "Conto prop scollegato: premi Connetti"
    : !s.brokerAccount?.isConnected
      ? bb
        ? "Conto broker B scollegato: premi Connetti"
        : "Conto broker scollegato: premi Connetti"
      : null;
  const blocker = s.state === "AWAITING_PAYOUT"
    ? /*
       * IN ATTESA DI PAYOUT IL PERIODO E' GIA' CHIUSO: un'operazione aperta
       * adesso produrrebbe un P&L che non ha un tratto dove andare. Il motivo
       * si dice per esteso — è lo stesso `SESSION_AWAITING_PAYOUT` con cui la
       * rotta rifiuterebbe — invece di lasciare un pulsante spento senza
       * spiegazione o un generico «fase chiusa», che manderebbe a cercare una
       * Fase finita mentre questa aspetta solo un bonifico.
       */
      "Fase in attesa di payout: il periodo è chiuso. Registra il payout arrivato e il tratto nuovo riparte"
    : hasOpen
    ? // Una coppia scoperta blocca per un motivo diverso, e il motivo va detto:
      // metterne un'altra sopra somma due rischi che nessuno sta guardando.
      anyDriftBlocks
      ? "Una coppia di questa fase ha uno scarto fra le gambe oltre la soglia: riallinea la gamba broker prima di aprirne un'altra"
      : "C'è già una coppia aperta in questa fase: chiudila per aprirne un'altra"
    : scollegato
    ? scollegato
    : symbols.error
      ? errorMessage(symbols.error)
      : !symbols.data
        ? "Caricamento dei simboli…"
        : !symbolEntry
          ? "Scegli il simbolo su cui aprire la coppia"
          : symbolEntry.blocker
            ? symbolEntry.blocker.message
            : hedgingMessage
              ? hedgingMessage
            : fieldError
              ? "Correggi i campi segnati: accettano solo numeri, con la virgola o col punto"
              : !complete
                ? bb
                  ? "Scrivi lotto e stop loss: li decidi a ogni operazione, il rapporto è quello della Fase"
                  : "Scrivi lotto, moltiplicatore e stop loss: la fase non li tiene, li decidi a ogni operazione"
              : preview.error
                ? errorMessage(preview.error)
                : (previewBlocker?.message ?? null);
  // Con `placeholderData` il piano resta a schermo mentre si ricalcola: dopo un
  // cambio di simbolo e' quello dell'ALTRO strumento, e non si apre su quello.
  // Sulla BB non c'e' una proiezione (nessun conto da bruciare): vale l'anteprima di questa stessa richiesta.
  const planIsForSymbol = preview.data?.projection ? preview.data.projection.canonicalSymbol === symbol : !!preview.data && !preview.isPlaceholderData && debounced === opKey;
  /*
   * SL E TP SUL GRAFICO PRIMA DI APRIRE: i prezzi sono quelli dell'anteprima
   * (`pointsFromMoney` + `planPair` nel backend, la strada dell'apertura), qui
   * si disegnano soltanto. Dove un numero manca o non e' affidabile, nessuna
   * linea e il motivo scritto: un prezzo inventato sul grafico e' peggio di nessuno.
   */
  const specIncomplete = (sp: PreviewSpec) => !(Number(sp.lotStep) > 0 && Number(sp.point) > 0 && Number(sp.valuePerPoint) > 0);
  const plannedNote = hasOpen || !symbol
    ? null
    : symbolEntry?.blocker
      ? `Nessuna linea: ${symbolEntry.blocker.message}`
      : !complete
        ? null
        : preview.error
          ? `Nessuna linea: ${errorMessage(preview.error)}`
          : !preview.data || !planIsForSymbol
            ? null
            : specIncomplete(preview.data.specs.prop) || specIncomplete(preview.data.specs.broker)
              ? "Nessuna linea: specifiche del simbolo incomplete (passo del lotto, punto o valore punto a zero). Sincronizza il simbolo dal server"
              : preview.data.planError
                ? `Nessuna linea: ${preview.data.planError}`
                : !preview.data.marketAvailable
                  ? "Nessuna linea: quotazione non letta, i prezzi di SL e TP non si calcolano"
                  : null;
  const planned = !hasOpen && complete && !plannedNote && planIsForSymbol ? (preview.data?.plan ?? null) : null;
  /*
   * UNA LINEA DEL PIANO LASCIATA SUL GRAFICO PROP: il prezzo va all'anteprima,
   * che risponde con l'importo (`fromPrice`, calcolato dal backend); il campo
   * lo riceve a 2 decimali e l'anteprima di sempre riparte da lì — Broker compreso.
   */
  const onPlannedDrop = async (level: "SL" | "TP", price: number) => {
    const r = await api.sessions.preview(s.id, {
      direction,
      lots: op.lots!,
      multiplier: op.mult!,
      canonicalSymbol: symbol,
      ...(level === "SL" ? { slPrice: price, ...(op.tp ? { tpMoney: op.tp } : {}) } : { slMoney: op.sl!, tpPrice: price }),
    });
    const m = level === "SL" ? r.fromPrice?.sl : r.fromPrice?.tp;
    if (m == null) throw new Error("l'anteprima non ha restituito l'importo");
    (level === "SL" ? setSlMoney : setTpMoney)(m.toFixed(2));
  };
  // Un'anteprima che dice «coppia aperta» e si sta rifacendo non abilita l'apertura: il motivo non si mostra, ma nemmeno si ignora.
  const canOpen = !hasOpen && complete && !!preview.data?.plan && planIsForSymbol && !blocker && !staleOpenBlocker;
  // Non blocca: la bruciatura è l'esito profittevole. Chiede solo un secondo clic.
  const burn = !hasOpen && !blocker ? (preview.data?.openWarning ?? null) : null;
  const armed = burn && burnConfirm;
  const stale = preview.isFetching || (complete && !!symbol && debounced !== opKey);
  const showSummary = !hasOpen && !!preview.data?.plan && !!preview.data?.money;

  const field = (label: string, value: string, set: (v: string) => void, opts: { suffix?: string; hint?: string } = {}) => (
    <DecimalField
      label={label}
      value={value}
      onChange={(v) => {
        set(v);
        setBurnConfirm(false);
      }}
      suffix={opts.suffix}
      hint={opts.hint}
    />
  );

  return (
    <div className="space-y-5">
      <PairView prop={s.propAccount} broker={s.brokerAccount} canWrite={canWrite} compact labels={bb ? BB_LEG_LABELS : undefined} />

      {!hasOpen && (
        <section className="space-y-3 rounded-card border border-line bg-surface p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-medium text-ink">Parametri dell'operazione</h2>
            <div role="radiogroup" aria-label={`Direzione sul ${legA}`} className="inline-flex overflow-hidden rounded border border-line">
              {(["LONG", "SHORT"] as const).map((d) => (
                <button
                  key={d}
                  role="radio"
                  aria-checked={direction === d}
                  onClick={() => {
                    setDirection(d);
                    setBurnConfirm(false);
                  }}
                  className={`px-4 py-1.5 text-sm ${direction === d ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
                >
                  {d === "LONG" ? `Long sul ${legA}` : `Short sul ${legA}`}
                </button>
              ))}
            </div>
          </div>

          <div className="max-w-sm space-y-1">
            <Field label="Simbolo">
              {(id) => (
                <select
                  id={id}
                  value={symbol}
                  disabled={!symbols.data}
                  onChange={(e) => {
                    setChosenSymbol(e.target.value);
                    setBurnConfirm(false);
                  }}
                  className={inputClass}
                >
                  {!symbol && (
                    <option value="" disabled>
                      Scegli il simbolo
                    </option>
                  )}
                  {symbolList.map((x) => (
                    // Spenti ma visibili: il motivo sta sotto il menu, una <option> non regge una frase.
                    <option key={x.canonicalSymbol} value={x.canonicalSymbol} disabled={!!x.blocker}>
                      {x.canonicalSymbol}
                      {x.isDefault ? " · predefinito" : ""}
                      {x.blocker ? " · non utilizzabile" : ""}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            {symbolList.flatMap((x) =>
              x.blocker ? [<p key={x.canonicalSymbol} className="text-xs text-warn">{x.canonicalSymbol}: {x.blocker.message}</p>] : [],
            )}
            {symbols.data && !symbolList.some((x) => x.isDefault) && (
              <p className="text-xs text-warn">
                Il simbolo predefinito della fase, {symbols.data.defaultSymbol}, non è mappato su tutt'e due i server: scegline un altro.
              </p>
            )}
            {!!symbols.data?.incomplete.length && (
              /*
               * NON DISPONIBILI, e perche': il simbolo e il server che manca —
               * lo stesso messaggio con cui l'apertura rifiuterebbe. «Mappa
               * adesso» apre la scelta del simbolo su QUEL server, coi candidati
               * letti dal server; chi non scrive vede il motivo e basta.
               */
              <ul className="space-y-0.5 text-xs text-faint">
                {symbols.data.incomplete.map((i) => (
                  <li key={i.canonicalSymbol}>
                    Non disponibile: {i.message}
                    {canWrite &&
                      i.missing.map((m) => (
                        <button
                          key={m.serverId}
                          type="button"
                          onClick={() => setMapNow({ serverId: m.serverId, canonical: i.canonicalSymbol })}
                          className="ml-2 text-accent hover:underline"
                        >
                          Mappa adesso{i.missing.length > 1 ? ` su ${m.server}` : ""}
                        </button>
                      ))}
                  </li>
                ))}
              </ul>
            )}
            {mapNowServer && mapNow && (
              <SymbolPanel server={mapNowServer} symbol={null} canonical={mapNow.canonical} peersOf={peersOf} autoDetect onClose={() => setMapNow(null)} />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {field(`Lotto sul ${legA}`, propLots, setPropLots)}
            {bb ? (
              <Field label="Rapporto di copertura" hint="Della Fase: si cambia dai Parametri.">
                {(id) => <input id={id} disabled value={ratioText(s.hedgeRatio)} className={inputClass} />}
              </Field>
            ) : (
              field("Moltiplicatore", multiplier, setMultiplier)
            )}
            {/* La valuta è quella del CONTO PROP, non un simbolo fisso. */}
            {field("Stop loss", slMoney, setSlMoney, { suffix: propCur })}
            {field("Take profit", tpMoney, setTpMoney, { suffix: propCur, hint: "Vuoto = nessun take profit." })}
          </div>

          {last && (
            <p className="text-xs text-faint">
              Precompilati con l'operazione n. {last.seq}, l'ultima di questa fase. Si cambiano a ogni apertura.
            </p>
          )}
        </section>
      )}

      {showSummary && (
        <div className="space-y-2">
          <CompactSummary s={s} preview={preview.data!} stale={stale} requestedMultiplier={op.mult} bb={bb} />
          <ValueEstimateNote v={preview.data!.valuePerPoint} />
          <PlanWarnings preview={preview.data!} bb={bb} />
          {!bb && <DailyDdWarning s={s} />}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {canWrite && !hasOpen && (
          <>
            <Button
              variant={armed ? "danger" : "primary"}
              disabled={!!busy || !canOpen}
              onClick={
                burn && !burnConfirm
                  ? () => setBurnConfirm(true)
                  : run("open", async () => {
                      setBurnConfirm(false);
                      return api.sessions.openPair(s.id, {
                        direction,
                        propLots: op.lots!,
                        brokerMultiplier: op.mult!,
                        canonicalSymbol: symbol,
                        slMoney: Number(op.sl),
                        ...(op.tp ? { tpMoney: Number(op.tp) } : {}),
                      });
                    })
              }
            >
              {busy === "open" ? "Apertura…" : armed ? "Conferma: apri l'operazione che può bruciare il conto" : "Apri operazione"}
            </Button>
            {armed && (
              <Button variant="ghost" onClick={() => setBurnConfirm(false)}>
                Annulla
              </Button>
            )}
          </>
        )}
        {blocker ? (
          <span role="status" className="text-sm text-warn">
            {sentence(blocker)}
          </span>
        ) : (
          !hasOpen && !preview.data && <span className="text-sm text-muted">Calcolo del piano…</span>
        )}
      </div>

      {/*
        * «MODIFICA I LIVELLI» SOPRA IL GRAFICO (09/10), subito sotto l'avviso
        * «C'è già una coppia aperta»: è il gesto che si fa guardando quella
        * coppia, e stava in fondo alla scheda «Operazione di trading», sotto i
        * due grafici. Le condizioni sono quelle di prima: una coppia aperta e
        * chi può scrivere.
        */}
      {canWrite &&
        openPairs.map((p) => {
          const lettura = s.levels?.[p.id] ?? null;
          return (
            <LevelsPanel
              key={p.id}
              s={s}
              pair={p}
              levels={lettura?.measured ? lettura : null}
              sl={levelsInput.sl}
              setSl={levelsInput.setSl}
              tp={levelsInput.tp}
              setTp={levelsInput.setTp}
            />
          );
        })}

      {/*
        * Gli eventi APERTI di questa fase, qui e non in una pagina da cercare.
        *
        * Sono la ragione per cui un'apertura viene rifiutata — livelli non
        * allineati, gamba scoperta, esecuzione fuori posto — e il posto in cui
        * leggerli e' quello in cui il rifiuto compare. Da qui si prendono anche
        * in carico: chiuderli altrove vorrebbe dire lasciare questa schermata.
        */}
      <SessionEvents sessionId={s.id} />

      {burn && (
        <div role="status" className="rounded-card border border-line bg-raised/50 px-4 py-3 text-sm text-ink">
          <p>{burn.message}.</p>
          <p className="mt-1 text-xs text-muted">
            Nel modello Match è l'esito atteso del ciclo: si apre normalmente, con un secondo clic di conferma.
          </p>
        </div>
      )}

      {/*
        * IL RIFIUTO CHE NON CHIEDE DI CORREGGERE NIENTE.
        *
        * `VALUE_PER_POINT_UNREADABLE` non è un campo sbagliato: è il broker che
        * non ha risposto al valore del punto, e l'apertura si è fermata invece
        * di usare quello salvato in mappatura — che sui simboli quotati in
        * un'altra valuta invecchia col cambio. Va detto che non è partito
        * nessun ordine e che i campi sono ancora quelli, o chi legge va a
        * cercare una coppia a metà che non esiste e riscrive tutto.
        */}
      {errorCode === "VALUE_PER_POINT_UNREADABLE" ? <ValueUnreadableNote action="apertura" message={error ?? ""} /> : <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="text-sm text-muted">{notice}</p>}
      {warnings.map((w) => (
        <p key={w} className="text-sm text-warn">
          {sentence(w)}
        </p>
      ))}

      {/*
        * I DUE GRAFICI, SOPRA LE SCHEDE: restano a schermo passando da
        * "Operazione di trading" a "Cronistoria", come il grafico di
        * MetaTrader che non si smonta cambiando pannello sotto. Per questo
        * vivono fuori da `PairTabs`, non dentro la sua scheda "trading".
        */}
      {/* Anche PRIMA di aprire: senza coppia sono le sole candele dello strumento scelto, per decidere. */}
      {(mainPair || symbol) && (
        <LevelsCharts
          s={s}
          pair={mainPair}
          symbol={symbol}
          canWrite={canWrite}
          sl={levelsInput.sl}
          tp={levelsInput.tp}
          slEditing={levelsInput.slEditing}
          tpEditing={levelsInput.tpEditing}
          onDrag={levelsInput.onDrag}
          planned={planned}
          plannedNote={plannedNote}
          onPlannedDrop={onPlannedDrop}
        />
      )}

      <PairTabs
        s={s}
        canWrite={canWrite}
        openPairs={openPairs}
        closedPairs={closedPairs}
        sl={levelsInput.sl}
        setSl={levelsInput.setSl}
        tp={levelsInput.tp}
        setTp={levelsInput.setTp}
      />

      {isFake && canWrite && (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-dashed border-line px-4 py-3 text-sm text-muted">
          <span>Mirror finto: simula il mercato per vedere il P&L muoversi</span>
          {[-500, -100, 100, 500].map((p) => (
            <Button key={p} size="sm" disabled={!!busy} onClick={run("move", () => api.dev.movePrice(p))}>
              {p > 0 ? `+${p}` : p} pt
            </Button>
          ))}
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={run("bootstrap", api.dev.mirrorBootstrap)}>
            {busy === "bootstrap" ? "Ripristino…" : "Ricollega i conti dal database"}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * IL RIEPILOGO COMPATTO: due righe corte al posto del testo lungo di prima.
 *
 * Lotti a mercato, SL e TP in punti, il rischio e il guadagno in denaro su
 * prop e broker, le operazioni residue nel budget di drawdown e la frizione
 * per operazione — quello che prima stava in quattro riquadri sta qui, e la
 * spiegazione di ciascun numero e' dietro il suo «?» (`Hint`), non piu' nel
 * testo corrente.
 */
function CompactSummary({ s, preview, stale, requestedMultiplier, bb = false }: { s: SessionDetail; preview: Preview; stale: boolean; requestedMultiplier: string | null | undefined; bb?: boolean }) {
  const plan = preview.plan!;
  const m = preview.money!;
  const propCur = s.propAccount.currency;
  const brokerCur = s.brokerAccount?.currency ?? propCur;
  const friction = plan.friction;

  const chiesto = m.requested;
  const arrotondato = chiesto !== null && Math.abs(chiesto.sl - m.atMarket.sl.prop) >= 0.005;
  // Il moltiplicatore effettivo si dice solo quando, a 2 decimali, non e' quello chiesto.
  const multChiesto = requestedMultiplier == null ? NaN : Number(requestedMultiplier);
  const multDiverso = Number.isFinite(multChiesto) && plan.effectiveMultiplier.toFixed(2) !== multChiesto.toFixed(2);

  return (
    <div className={`rounded border border-line bg-raised/40 p-3 text-sm transition-opacity ${stale ? "opacity-60" : ""}`} aria-live="polite">
      <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-ink">
        <span>
          {lots(plan.prop.lots)} contro {lots(plan.broker.lots)} lotti
        </span>
        <span className="text-faint">·</span>
        <span>SL {points(plan.prop.slPoints)}</span>
        <span className="text-faint">·</span>
        <span>TP {plan.prop.tpPoints === 0 ? "nessuno" : points(plan.prop.tpPoints)}</span>
        {/* Broker contro broker: nessun budget di drawdown da contare. */}
        {!bb && (
          <>
            <span className="text-faint">·</span>
            <span className="inline-flex items-center">
              residue oggi <span className="ml-1 font-medium">{plan.remainingOps}</span>
              <Hint>
                Operazioni residue nel budget di drawdown prima di esaurirlo, a parità di lotto, moltiplicatore e stop loss di
                questa. Oggi questa fase ne ha già fatte {s.summary.opsToday}.
              </Hint>
            </span>
          </>
        )}
        {multDiverso && (
          <>
            <span className="text-faint">·</span>
            <span className="inline-flex items-center">
              moltiplicatore effettivo {plan.effectiveMultiplier.toFixed(4)}
              <Hint>Dopo l'arrotondamento del lotto broker al suo lot step.</Hint>
            </span>
          </>
        )}
      </p>
      <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        <span className="text-faint">A stop loss</span>
        <span className="text-prop">{money(m.atMarket.sl.prop, propCur)}</span>
        <span className="text-broker">{money(m.atMarket.sl.broker, brokerCur)}</span>
        {arrotondato && chiesto && (
          <Hint>
            Stop loss chiesto {money(chiesto.sl, chiesto.currency)}: i punti vanno per difetto, quindi a mercato il rischio
            è {money(m.atMarket.sl.prop, propCur)}.
          </Hint>
        )}
        {plan.prop.tpPoints > 0 && (
          <>
            <span className="ml-1 text-faint">· A take profit</span>
            <span className="text-prop">{money(m.atMarket.tp.prop, propCur)}</span>
            <span className="text-broker">{money(m.atMarket.tp.broker, brokerCur)}</span>
          </>
        )}
        {friction && (
          <>
            <span className="ml-1 text-faint">· frizione</span>
            <span>
              <ConvertedAmount value={friction.perOpOnReal} />
            </span>
            <Hint>
              Sul reale pesano i due spread sui lotti broker e la commissione del broker: spread {friction.prop.spreadPoints} +{" "}
              {friction.broker.spreadPoints} pt = {money(friction.broker.spreadCost.amount, friction.broker.spreadCost.currency)} · commissione
              broker {money(friction.broker.commission.total.amount, friction.broker.commission.total.currency)}
              {chargeLabel(friction.broker.commission)}
              {friction.prop.commission.total.amount > 0 ? ` · prop ${money(friction.prop.commission.total.amount, friction.prop.commission.total.currency)} sul DD` : ""}.
              {preview.commissionSource.prop === "UNKNOWN"
                ? " Commissione del prop non ancora misurata: la proiezione la conta zero."
                : preview.commissionSource.prop === "ESTIMATE"
                  ? " Commissione del prop stimata a mano: non ancora letta dai deal."
                  : ""}
            </Hint>
            {/* Lo spread ATTUALE di ciascuna gamba: denaro (valuta della gamba) e punti. Il denaro lo calcola il backend. */}
            <span className="ml-1 text-faint">· spread</span>
            <span className="text-prop">
              {money(friction.prop.spreadCost.amount, friction.prop.spreadCost.currency)}{" "}
              <span className="text-faint">({points(friction.prop.spreadPoints)})</span>
            </span>
            <span className="text-broker">
              {money(friction.broker.legSpreadCost.amount, friction.broker.legSpreadCost.currency)}{" "}
              <span className="text-faint">({points(friction.broker.spreadPoints)})</span>
            </span>
            <Hint>
              Lo spread di adesso su ciascuna gamba, dalle quotazioni lette: punti × lotti della gamba × valore punto, nella valuta del suo
              conto. Non si somma alla frizione qui accanto: sul reale pesano i due spread sui lotti broker.
            </Hint>
          </>
        )}
      </p>
    </div>
  );
}

/** Gli avvisi del piano: restano in chiaro, perché sono sicurezza e non spiegazione. */
function PlanWarnings({ preview, bb = false }: { preview: Preview; bb?: boolean }) {
  const plan = preview.plan!;
  return (
    <>
      {!preview.marketAvailable && <p className="text-xs text-faint">Quotazioni non disponibili: connetti i due conti per vedere prezzi, livelli e frizione.</p>}
      {/* Lo stopsLevel si legge PRIMA di premere: se è letto dal broker blocca, se è scritto a mano avvisa. */}
      {preview.stopsLevel.map((v) => (
        <p key={`${v.role}-${v.level}`} className={`text-xs ${v.live ? "text-loss" : "text-warn"}`} role="status">
          {v.level} della gamba {bb ? (v.role === "PROP" ? "broker A" : "broker B") : v.role === "PROP" ? "prop" : "broker"} a {points(v.points)}: {v.brokerSymbol} ne richiede almeno {points(v.stopsLevel)}.{" "}
          {v.live ? "Il server rifiuterebbe l'ordine." : "Valore scritto a mano nella pagina Server: controllalo."}
        </p>
      ))}
      {preview.lotStepWarning && (
        <p className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn" role="status">
          {preview.lotStepWarning.message}
        </p>
      )}
      {plan.warnings.map((w) => (
        <p key={w} className="text-sm text-warn">
          {sentence(w)}
        </p>
      ))}
      {/*
        * Quello che il server ha RIFIUTATO, separato da quello che dichiara.
        *
        * Sono due nature diverse: sopra ci sono gli avvisi che il motore ricava
        * dalle specifiche del broker, qui c'è un'osservazione raccolta sul
        * campo. Avvisa e non blocca — un rifiuto dice «più di N», non «N», e
        * può essere stato causato da tutt'altro.
        */}
      {(preview.stopRejections ?? []).map((w) => (
        <p key={w} className="text-sm text-warn">
          {w}
        </p>
      ))}
    </>
  );
}

/**
 * L'AVVISO DEL DRAWDOWN GIORNALIERO, sotto il riepilogo.
 *
 * `dailyDdMoney` null = la prop non ne prevede uno: niente da mostrare, e non
 * una barra a zero che sembra un limite che non c'è.
 */
function DailyDdWarning({ s }: { s: SessionDetail }) {
  const sum = s.summary;
  if (sum.dailyDdMoney === null) return null;
  const cur = s.propAccount.currency;
  const ratio = !sum.dayBoundaryRead || sum.dailyDdRemaining === null ? null : Math.max(0, Math.min(1, 1 - sum.dailyDdRemaining / sum.dailyDdMoney));
  const detail = !sum.dayBoundaryRead
    ? `limite ${money(sum.dailyDdMoney, cur)} al giorno, fuso del server prop non ancora letto`
    : sum.dailyDdRemaining === null
      ? "—"
      : `${money(sum.dailyDdRemaining, cur)} di ${money(sum.dailyDdMoney, cur)}`;
  return <Progress label="DD giornaliero residuo" tone="prop" ratio={ratio} detail={detail} />;
}

/**
 * DA DOVE VIENE IL VALORE DEL PUNTO, in due parole accanto al numero.
 *
 * Il `tickValue` di MT5 è nella valuta di DEPOSITO del conto: sui simboli
 * quotati in un'altra valuta il gateway lo converte al cambio di adesso, quindi
 * il valore punto **si muove**, e quello salvato in mappatura è la fotografia
 * dell'ultima sincronizzazione. Leggere «1 USD» senza sapere quale dei due è
 * vuol dire non sapere se gli importi accanto sono di oggi.
 */
function ValueSourceTag({ v }: { v: ValueInUse }) {
  return v.source === "LIVE" ? <span>dal broker</span> : <span className="text-warn">stima (salvato)</span>;
}

/**
 * IL RIFIUTO CHE NON CHIEDE DI CORREGGERE NIENTE, e vale per le due rotte che
 * mandano ordini: l'apertura e la modifica dei livelli.
 *
 * `VALUE_PER_POINT_UNREADABLE` non è un campo sbagliato: è il broker che non ha
 * risposto al valore del punto, e l'operazione si è fermata invece di usare
 * quello salvato in mappatura — che sui simboli quotati in un'altra valuta
 * invecchia col cambio. Va detto che non è partito niente e che i campi sono
 * ancora quelli, o chi legge va a cercare una coppia a metà che non esiste e
 * riscrive tutto.
 */
function ValueUnreadableNote({ action, message }: { action: "apertura" | "modifica"; message: string }) {
  return (
    <div role="alert" className="rounded border border-loss/40 bg-loss/10 px-3 py-2 text-sm text-ink">
      <p className="font-medium text-loss">
        {action === "apertura" ? "Operazione non aperta" : "Livelli non modificati"}: il valore del punto non si è letto dal
        broker
      </p>
      <p className="mt-1">{sentence(message)}</p>
      <p className="mt-1 text-xs text-muted">
        {action === "apertura"
          ? "Non è stato mandato nessun ordine"
          : "Non è stata toccata nessuna delle due gambe"}{" "}
        e i campi restano come li hai scritti: riprova fra un istante. Con il valore salvato in mappatura lo stop loss
        varrebbe un importo diverso da quello che hai scritto, e non se ne accorgerebbe nessuno.
      </p>
    </div>
  );
}

/**
 * L'anteprima calcolata su un valore punto vecchio lo DICE, e non in un
 * dettaglio in fondo.
 *
 * L'anteprima non si rifiuta di rispondere — una schermata vuota al posto di
 * numeri quasi giusti sarebbe peggio — ma la differenza fra «rischi 199,50» e
 * «rischieresti 199,50 al cambio dell'ultima sincronizzazione» è tutta qui.
 * L'apertura invece si ferma, e la sua frase è più sotto.
 */
function ValueEstimateNote({ v }: { v: ValuesInUse }) {
  if (!v.estimated || !v.message) return null;
  return (
    <div role="status" className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
      <p className="font-medium">Numeri calcolati su una stima</p>
      <p className="mt-1">{sentence(v.message)}</p>
      <p className="mt-1 text-xs text-muted">
        L'apertura il valore punto lo pretende letto dal broker: se non ci riesce rifiuta l'operazione, invece di aprirla
        con quello salvato.
      </p>
    </div>
  );
}

/**
 * LE DUE SCHEDE SOTTO LA POSTAZIONE: quello che è a mercato adesso, e quello
 * che è già stato chiuso in questa fase. Si apre sulla cronistoria quando non
 * c'è niente a mercato: è la scheda con qualcosa da leggere.
 */
function PairTabs({
  s,
  canWrite,
  openPairs,
  closedPairs,
  sl,
  setSl,
  tp,
  setTp,
}: {
  s: SessionDetail;
  canWrite: boolean;
  openPairs: HedgePair[];
  closedPairs: HedgePair[];
  sl: string;
  setSl: (v: string) => void;
  tp: string;
  setTp: (v: string) => void;
}) {
  /*
   * LA SCHEDA SEGUE I DATI FINCHE' NON SCEGLIE LA PERSONA (09/10).
   *
   * Prima si sceglieva una volta sola, al montaggio: se in quel momento le
   * coppie non erano ancora arrivate (o la coppia si apriva a pagina gia'
   * aperta) restava «Cronistoria» per sempre, anche con un'operazione a
   * mercato. Adesso, con una coppia aperta, vale «Operazione di trading»; un
   * clic su una scheda vince su tutto.
   */
  const [scelta, setTab] = useState<"trading" | "storia" | null>(null);
  const tab = scelta ?? (openPairs.length > 0 ? "trading" : "storia");

  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="Coppie della fase" className="inline-flex overflow-hidden rounded border border-line text-sm">
        <button
          role="tab"
          aria-selected={tab === "trading"}
          onClick={() => setTab("trading")}
          className={`px-3 py-1.5 ${tab === "trading" ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
        >
          Operazione di trading{openPairs.length > 0 ? ` (${openPairs.length})` : ""}
        </button>
        <button
          role="tab"
          aria-selected={tab === "storia"}
          onClick={() => setTab("storia")}
          className={`px-3 py-1.5 ${tab === "storia" ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
        >
          Cronistoria{closedPairs.length > 0 ? ` (${closedPairs.length})` : ""}
        </button>
      </div>

      {tab === "trading" ? (
        openPairs.length > 0 ? (
          <div className="space-y-5">
            {openPairs.map((p) => (
              <OpenPairCard key={p.id} s={s} pair={p} canWrite={canWrite} levels={s.levels?.[p.id] ?? null} sl={sl} setSl={setSl} tp={tp} setTp={setTp} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted">Niente a mercato in questo momento.</p>
        )
      ) : (
        <History
          sessionId={s.id}
          bb={isBrokerPair(s)}
          // Quando una coppia si chiude, o il suo P&L arriva dopo, il dettaglio della fase cambia e la cronistoria si rilegge.
          version={closedPairs.map((p) => `${p.id}:${p.closedAt ?? ""}:${p.fxRateAtClose ?? ""}:${p.legs.map((l) => l.pnlMt5 ?? "").join("/")}`).join(",")}
        />
      )}
    </div>
  );
}

/** Il P&L di una gamba chiusa: verde in profitto, rosso in perdita, alla dimensione del resto della riga. */
function LegPnlInline({ leg }: { leg: HedgeLeg | undefined }) {
  if (!leg) return <Dash />;
  if (leg.pnlEffective === null) return <Dash title="Gamba non chiusa" />;
  return <span className={`font-medium tabular-nums ${signClass(Number(leg.pnlEffective))}`}>{money(leg.pnlEffective, leg.currency)}</span>;
}

/** Le coppie che non sono finite bene: si riconoscono dal colore, non solo dalla parola. */
const FAILED_PAIR_STATES: readonly string[] = ["FAILED_OPEN", "ABANDONED", "ONE_LEG"];

const pnlOf = (p: HedgePair, role: "PROP" | "BROKER") => {
  const v = p.legs.find((l) => l.accountRole === role)?.pnlEffective;
  return v === null || v === undefined ? null : Number(v);
};

/** Oggi a Roma, «AAAA-MM-GG»: lo stesso giorno su cui filtra il backend. */
const romeToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
/** Un giorno di calendario spostato di `n` giorni: aritmetica sul calendario, senza fuso. */
const shiftDay = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Un totale della cronistoria: l'importo, o «incompleto» col perché. Mai un numero che sembra esatto quando non lo è. */
function HistoryTotal({ label, tone, net, currency, missing }: { label: ReactNode; tone?: "prop" | "broker"; net: string | null; currency: string | null; missing: string | null }) {
  return (
    <div className={`p-3 ${tone === "prop" ? "pair-side-prop" : tone === "broker" ? "pair-side-broker" : ""}`}>
      <p className={`text-xs font-medium ${tone === "prop" ? "text-prop" : tone === "broker" ? "text-broker" : "text-muted"}`}>{label}</p>
      {net === null || currency === null ? (
        <>
          <p className="font-medium text-loss">Incompleto</p>
          {missing && <p className="text-xs text-muted">{missing}</p>}
        </>
      ) : (
        <p className={`font-medium tabular-nums ${signClass(Number(net))}`}>{money(net, currency)}</p>
      )}
    </div>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** «+10,00 USD»: una rettifica porta sempre il segno. */
const signedMoney = (amount: string, currency: string) => `${Number(amount) > 0 ? "+" : ""}${money(amount, currency)}`;

/** Una riga della cronistoria: una coppia di questa fase, o un'altra operazione di uno dei due conti. */
type HistoryRow = { k: "PAIR"; id: string; pair: HedgePair } | { k: "ROW"; id: string; row: AccountHistoryRow };

/** Le righe della dashboard si vedono a colpo d'occhio: coppie, singole, gambe di altre fasi. Le esterne no. */
const DASHBOARD_ROW = "bg-accent/5";

/** Il P&L di una riga del conto nella SUA colonna; nell'altra niente, non un trattino che sembri uno zero. */
function RowPnl({ row, role }: { row: AccountHistoryRow; role: "PROP" | "BROKER" }) {
  if (row.role !== role) return null;
  if (row.net === null) return <Dash title="P&L non letto" />;
  return <span className={`font-medium tabular-nums ${signClass(Number(row.net))}`}>{money(row.net, row.currency)}</span>;
}

/** Il totale del conto: l'importo, o «incompleto» con TUTTI i perché. */
const totalMissing = (t: AccountHistoryTotal) => (t.missing.length ? t.missing.join(" · ") : null);

/**
 * La cronistoria: le coppie chiuse della fase nel periodo scelto, col
 * riepilogo sopra. Filtro sul giorno di CHIUSURA a Roma; lista e totali li
 * calcola il backend sullo stesso filtro, qui non si somma niente. Sola lettura.
 *
 * COMPLETA DAL 09/10: con «Tutte» compaiono anche le altre operazioni dei due
 * conti — le singole, le gambe di altre fasi sullo stesso conto e le
 * operazioni ESTERNE (manuali o di altri), lette dallo storico del gateway
 * solo se il conto e' collegato: la pagina non collega. Le righe della
 * dashboard hanno lo sfondo, le esterne no e portano la scritta «esterna», nella
 * sola colonna del loro conto. Due totali: «solo dashboard» (le coppie della
 * fase, come prima) e «totale del conto».
 */
function History({ sessionId, version, bb = false }: { sessionId: string; version: string; bb?: boolean }) {
  const today = romeToday();
  // I nomi delle due gambe: sulla Fase broker contro broker sono il broker A e il broker B.
  const nA = bb ? "broker A" : "prop";
  const nB = bb ? "broker B" : "broker";
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(today);
  const [vista, setVista] = useState<"TUTTE" | "DASHBOARD">("TUTTE");
  const q = useQuery({
    queryKey: ["session-closed-pairs", sessionId, "account", from, to, version],
    queryFn: () => api.sessions.accountHistory(sessionId, { from: from || undefined, to: to || undefined }),
    placeholderData: (prev) => prev,
    // Lo storico del gateway e' una chiamata vera: non a ogni messa a fuoco della finestra.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const shortcuts: { label: string; from: string }[] = [
    { label: "Oggi", from: today },
    { label: "7 giorni", from: shiftDay(today, -6) },
    { label: "30 giorni", from: shiftDay(today, -29) },
    { label: "Dall'inizio della fase", from: "" },
  ];
  const t = q.data?.totals;
  const missingPnl = (n: number) => (n ? `${plural(n, "gamba", "gambe")} senza P&L` : null);

  const sideText = (r: AccountHistoryRow) => `${r.side === "BUY" ? "Acquisto" : r.side === "SELL" ? "Vendita" : ""} ${r.lots ? lots(r.lots) : ""}`.trim();
  const rowPnl = (r: AccountHistoryRow, role: "PROP" | "BROKER") => (r.role === role && r.net !== null ? Number(r.net) : null);
  const columns: DataTableColumn<HistoryRow>[] = [
    { key: "seq", header: "#", cell: (h) => <span className="text-muted">{h.k === "PAIR" ? h.pair.seq : ""}</span>, sortBy: (h) => (h.k === "PAIR" ? h.pair.seq : null) },
    { key: "symbol", header: "Simbolo", cell: (h) => (h.k === "PAIR" ? h.pair.canonicalSymbol : h.row.symbol), sortBy: (h) => (h.k === "PAIR" ? h.pair.canonicalSymbol : h.row.symbol) },
    {
      key: "direction",
      header: "Direzione",
      cell: (h) => (h.k === "PAIR" ? `${h.pair.propDirection === "LONG" ? "Long" : "Short"} sul ${nA}` : sideText(h.row)),
      sortBy: (h) => (h.k === "PAIR" ? h.pair.propDirection : h.row.side),
    },
    {
      key: "state",
      header: "Esito",
      cell: (h) =>
        h.k === "ROW" ? (
          <span className={h.row.kind === "EXTERNAL" ? "rounded bg-raised px-1.5 py-0.5 text-xs text-muted" : "text-muted"}>{h.row.label}</span>
        ) : FAILED_PAIR_STATES.includes(h.pair.state) ? (
          <span className="font-medium text-loss">{LABELS.pairState[h.pair.state]}</span>
        ) : (
          <span className="text-muted">{LABELS.pairState[h.pair.state]}</span>
        ),
      sortBy: (h) => (h.k === "PAIR" ? h.pair.state : h.row.label),
    },
    {
      key: "pnlProp",
      header: <span className="text-prop">P&L {nA}</span>,
      align: "right",
      cell: (h) => (h.k === "PAIR" ? <LegPnlInline leg={h.pair.legs.find((l) => l.accountRole === "PROP")} /> : <RowPnl row={h.row} role="PROP" />),
      sortBy: (h) => (h.k === "PAIR" ? pnlOf(h.pair, "PROP") : rowPnl(h.row, "PROP")),
    },
    {
      key: "pnlBroker",
      header: <span className="text-broker">P&L {nB}</span>,
      align: "right",
      cell: (h) => (h.k === "PAIR" ? <LegPnlInline leg={h.pair.legs.find((l) => l.accountRole === "BROKER")} /> : <RowPnl row={h.row} role="BROKER" />),
      sortBy: (h) => (h.k === "PAIR" ? pnlOf(h.pair, "BROKER") : rowPnl(h.row, "BROKER")),
    },
    {
      key: "reason",
      header: "Causa di chiusura",
      cell: (h) => (h.k === "PAIR" && h.pair.closeReason ? LABELS.closeReason[h.pair.closeReason] : h.k === "PAIR" ? <Dash /> : null),
      sortBy: (h) => (h.k === "PAIR" && h.pair.closeReason ? LABELS.closeReason[h.pair.closeReason] : null),
    },
    { key: "opened", header: "Aperta", cell: (h) => <span className="text-muted">{dateTime(h.k === "PAIR" ? h.pair.openedAt : h.row.openedAt)}</span>, sortBy: (h) => (h.k === "PAIR" ? h.pair.openedAt : h.row.openedAt) },
    { key: "closed", header: "Chiusa", cell: (h) => <span className="text-muted">{dateTime(h.k === "PAIR" ? h.pair.closedAt : h.row.closedAt)}</span>, sortBy: (h) => (h.k === "PAIR" ? h.pair.closedAt : h.row.closedAt) },
  ];
  const coppie: HistoryRow[] = (q.data?.pairs ?? []).map((pair) => ({ k: "PAIR", id: pair.id, pair }));
  const altre: HistoryRow[] = (q.data?.rows ?? []).map((row) => ({ k: "ROW", id: row.id, row }));
  const righe = vista === "DASHBOARD" ? coppie : [...coppie, ...altre];
  const esterne = altre.filter((h) => h.k === "ROW" && h.row.kind === "EXTERNAL").length;
  const nonLetti = (q.data?.accounts ?? []).filter((a) => a.status !== "READ");
  const at = q.data?.accountTotals;
  const combinedMissing = t
    ? [t.combined.fxMissing ? `${plural(t.combined.fxMissing, "coppia", "coppie")} senza tasso` : null, missingPnl(t.prop.missingPnl + t.broker.missingPnl)].filter(Boolean).join(" · ") || null
    : null;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Dal">{(id) => <input id={id} type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={`${inputClass} py-1.5`} />}</Field>
        <Field label="Al">{(id) => <input id={id} type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={`${inputClass} py-1.5`} />}</Field>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Periodo">
          {shortcuts.map((sc) => {
            const active = from === sc.from && to === today;
            return (
              <Button key={sc.label} size="sm" variant={active ? "secondary" : "ghost"} aria-pressed={active} onClick={() => {
                  setFrom(sc.from);
                  setTo(today);
                }}>
                {sc.label}
              </Button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted">Giorno di chiusura, ora italiana.</p>
        <div role="radiogroup" aria-label="Quali operazioni" className="inline-flex overflow-hidden rounded border border-line text-sm">
          {(
            [
              ["TUTTE", `Tutte${altre.length ? ` (${coppie.length + altre.length})` : ""}`],
              ["DASHBOARD", "Solo dashboard"],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={vista === v}
              onClick={() => setVista(v)}
              className={`px-3 py-1.5 ${vista === v ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {q.isError && <ErrorNote>{errorMessage(q.error)}</ErrorNote>}

      {nonLetti.map((a) => (
        <p key={a.accountId} role="status" className={`text-sm ${a.status === "NOT_CONNECTED" ? "text-muted" : "text-warn"}`}>
          <span className={a.role === "PROP" ? "text-prop" : "text-broker"}>{a.label}</span>: {a.message}
          {a.status === "NOT_CONNECTED" && "."}
        </p>
      ))}

      {t && (
        <div className="card grid grid-cols-2 sm:grid-cols-4">
          <div className="p-3">
            <p className="text-xs font-medium text-muted">
              Solo dashboard
              <Hint>Le coppie di questa fase, come la cronistoria di prima. Le singole e le altre operazioni del conto stanno nel totale del conto.</Hint>
            </p>
            <p className="font-medium tabular-nums">{plural(t.count, "operazione", "operazioni")}</p>
          </div>
          <HistoryTotal label={`P&L netto ${nA}`} tone="prop" net={t.prop.net} currency={t.prop.currency} missing={missingPnl(t.prop.missingPnl)} />
          <HistoryTotal
            label={`P&L netto ${nB}`}
            tone="broker"
            net={t.broker.net}
            currency={t.broker.currency}
            missing={t.broker.mixedCurrencies ? `Conti in valute diverse (${t.broker.mixedCurrencies.join(", ")})` : missingPnl(t.broker.missingPnl)}
          />
          <HistoryTotal
            label={
              <>
                Totale combinato ({t.combined.currency})
                <Hint>Il {nB} convertito in valuta del {nA}, ogni coppia al tasso congelato alla sua chiusura (1 se le valute coincidono). Netto = profitto + swap + commissioni + eventuali rettifiche, come nella colonna.</Hint>
              </>
            }
            net={t.combined.net}
            currency={t.combined.currency}
            missing={combinedMissing}
          />
        </div>
      )}

      {at && (
        <div className="card grid grid-cols-2 sm:grid-cols-4">
          <div className="p-3">
            <p className="text-xs font-medium text-muted">Totale del conto</p>
            <p className="text-xs text-muted">
              {plural(coppie.length + altre.length, "operazione", "operazioni")}
              {esterne > 0 && `, ${plural(esterne, "esterna", "esterne")}`}
            </p>
          </div>
          <HistoryTotal label={`Conto ${nA}`} tone="prop" net={at.prop.net} currency={at.prop.currency} missing={totalMissing(at.prop)} />
          {at.broker ? (
            <HistoryTotal label={`Conto ${nB}`} tone="broker" net={at.broker.net} currency={at.broker.currency} missing={totalMissing(at.broker)} />
          ) : (
            <div className="p-3 pair-side-broker">
              <p className="text-xs font-medium text-broker">Conto {nB}</p>
              <p className="text-sm text-muted">Fase senza {nB}</p>
            </div>
          )}
          <HistoryTotal
            label={
              <>
                Totale combinato ({at.combined.currency})
                <Hint>{at.combined.rateNote}</Hint>
              </>
            }
            net={at.combined.net}
            currency={at.combined.currency}
            missing={totalMissing(at.combined)}
          />
        </div>
      )}

      {t && (t.prop.adjustments.pairs > 0 || t.broker.adjustments.pairs > 0) && (
        <p className="text-xs text-muted">
          Rettifiche incluse:{" "}
          {t.prop.adjustments.pairs > 0 && (
            <span className="text-prop">
              {nA} {plural(t.prop.adjustments.pairs, "coppia", "coppie")}, <span className="tabular-nums">{signedMoney(t.prop.adjustments.amount, t.prop.currency)}</span>
            </span>
          )}
          {t.prop.adjustments.pairs > 0 && t.broker.adjustments.pairs > 0 && " · "}
          {t.broker.adjustments.pairs > 0 && (
            <span className="text-broker">
              {nB} {plural(t.broker.adjustments.pairs, "coppia", "coppie")}
              {t.broker.adjustments.amount !== null && t.broker.currency !== null && (
                <>
                  , <span className="tabular-nums">{signedMoney(t.broker.adjustments.amount, t.broker.currency)}</span>
                </>
              )}
            </span>
          )}
        </p>
      )}

      {q.data && q.data.undated > 0 && (
        <p className="text-xs text-muted">
          {plural(q.data.undated, "coppia abbandonata", "coppie abbandonate")} senza data di chiusura: non {q.data.undated === 1 ? "entra" : "entrano"} in nessun periodo.
        </p>
      )}

      <div className="card">
        <DataTable
          bare
          columns={columns}
          rows={righe}
          rowKey={(h) => h.id}
          initialSort={{ key: "closed", dir: "desc" }}
          rowClassName={(h) => (h.k === "PAIR" ? (FAILED_PAIR_STATES.includes(h.pair.state) ? "bg-loss/5" : DASHBOARD_ROW) : h.row.kind === "EXTERNAL" ? "" : DASHBOARD_ROW)}
          empty={
            q.isPending
              ? { title: "Caricamento…", hint: "Lettura delle coppie chiuse nel periodo." }
              : from || to !== today
                ? { title: "Nessuna coppia chiusa in questo periodo", hint: "Allarga il periodo, o scegli «Dall'inizio della fase»." }
                : { title: "Nessuna coppia chiusa in questa fase", hint: "Le coppie chiuse compaiono qui, con il P&L delle due gambe e la causa di chiusura." }
          }
        />
      </div>
    </div>
  );
}

/** «-1.270,40 USD (4278.86)»: l'importo in denaro e il livello fra parentesi; senza l'importo, il solo livello; senza livello, un trattino. */
function levelText(amount: number | null | undefined, level: string | null, currency: string): string {
  if (level === null) return "—";
  return amount === null || amount === undefined ? level : `${money(amount, currency)} (${level})`;
}

function OpenPairCard({
  s,
  pair,
  canWrite,
  levels,
  sl,
  setSl,
  tp,
  setTp,
}: {
  s: SessionDetail;
  pair: HedgePair;
  canWrite: boolean;
  levels: LevelsReading | null;
  sl: string;
  setSl: (v: string) => void;
  tp: string;
  setTp: (v: string) => void;
}) {
  const legView = (leg: HedgeLeg | undefined, tone: "prop" | "broker") => {
    if (!leg) return <section className="p-4" />;
    const l = s.live[leg.id];
    const gm = s.money?.[pair.id]?.atMarket[tone];
    return (
      <section className={`p-4 ${tone === "prop" ? "pair-side-prop" : "pair-side-broker"}`}>
        <p className={`text-xs font-medium ${tone === "prop" ? "text-prop" : "text-broker"}`}>
          {isBrokerPair(s) ? BB_LEG_LABELS[tone === "prop" ? "PROP" : "BROKER"] : tone === "prop" ? "Prop" : "Broker"} · {leg.brokerSymbol} · ticket <span className="machine-id">{leg.mt5Ticket ?? "—"}</span>
        </p>
        {/*
          * IL P&L PRIMA DI TUTTO: verde in profitto, rosso in perdita, alla dimensione di una cifra normale.
          * Non passa da `Auto`: quello lo rende `italic text-muted` e, essendo il figlio, il grigio
          * batteva il colore del segno messo qui sul <p>.
          */}
        <p className={`mt-1 text-lg font-semibold tabular-nums ${l ? signClass(l.profit) : "text-muted"}`} title={l ? "Rilevato dal gateway" : undefined}>
          {l ? money(l.profit, leg.currency) : "P&L live non disponibile"}
        </p>
        <p className="mt-0.5 text-sm text-muted">
          {leg.side === "BUY" ? "Acquisto" : "Vendita"} {lots(leg.lots)}
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Stat label="Prezzo di apertura">{leg.openPrice ?? "—"}</Stat>
          <Stat label="Prezzo attuale">{l?.price ?? "—"}</Stat>
          {/* Il denaro lo calcola il backend (`money.atMarket`), il livello fra parentesi: es. «-1.270,40 USD (4278.86)». */}
          <Stat label="Stop loss">{levelText(gm?.sl, leg.slPrice, gm?.currency ?? leg.currency)}</Stat>
          <Stat label="Take profit">{levelText(gm?.tp, leg.tpPrice, gm?.currency ?? leg.currency)}</Stat>
        </dl>
        {l?.quoteError && (
          <p className="mt-2 text-xs text-warn" role="status">
            Posizione a mercato, prezzo non letto: {l.quoteError}.
          </p>
        )}
        {leg.orderReason && leg.orderReason !== "CLIENT" && <p className="mt-2 text-xs text-loss">Origine ordine {leg.orderReason}: atteso CLIENT</p>}
      </section>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-medium text-ink">
          Coppia #{pair.seq} · {pair.canonicalSymbol} · {pair.propDirection === "LONG" ? "Long" : "Short"} sul {isBrokerPair(s) ? "broker A" : "prop"} · {LABELS.pairState[pair.state]} · aperta {dateTime(pair.openedAt)}
        </h2>
        {canWrite && (
          <span className="inline-flex items-center gap-1">
            <ClosePairButton sessionId={s.id} pair={pair} />
            {/*
              * "Abbandona" nel menu "…": e' l'uscita per un guasto (il gateway
              * non risponde e la coppia resta appesa), non un'alternativa a
              * "Chiudi coppia". `MoreMenu` non si chiude al clic dentro: serve
              * al secondo clic di conferma che sta in `AbandonMenuItem`.
              */}
            <MoreMenu label="Altre azioni sulla coppia">
              <AbandonMenuItem sessionId={s.id} pair={pair} />
            </MoreMenu>
          </span>
        )}
      </div>
      {/* «Non misurata» non e' «in ordine»: senza misura non si dice niente sulle gambe, e lo si dice forte. */}
      {levels && !levels.measured && (
        <div role="alert" className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
          <p className="font-medium">Livelli non misurati</p>
          <p className="mt-1 text-xs">{levels.message}</p>
        </div>
      )}
      <div className="pair">
        {legView(pair.legs.find((l) => l.accountRole === "PROP"), "prop")}
        {legView(pair.legs.find((l) => l.accountRole === "BROKER"), "broker")}
      </div>
      {/* «Modifica i livelli» non sta piu' qui: e' sopra i grafici, in `SessionTrade`. */}
    </div>
  );
}

/**
 * "Abbandona", DENTRO IL MENU "…": non e' un secondo modo di chiudere la
 * coppia, e' l'uscita quando il gateway non risponde piu' e la coppia resta
 * appesa — le posizioni potrebbero essere ancora a mercato, e qui non si
 * chiude niente: si dichiara solo che non sappiamo dove sia finita.
 */
function AbandonMenuItem({ sessionId, pair }: { sessionId: string; pair: HedgePair }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  const abandon = async () => {
    setBusy(true);
    setError(null);
    setEsito(null);
    try {
      const r = await api.sessions.abandonPair(sessionId, pair.id);
      setConfirm(false);
      setEsito(
        r.outcome === "CONSOLIDATED"
          ? "Il gateway ha risposto e non c'era piu' niente a mercato: la coppia e' stata consolidata, col suo P&L. Non e' stata abbandonata."
          : "Coppia abbandonata. L'evento resta aperto: controlla dal terminale MT5 che non ci siano posizioni ancora a mercato.",
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  if (esito) return <p className="px-3 py-1.5 text-xs text-muted">{esito}</p>;

  if (confirm) {
    return (
      <div className="space-y-1">
        <Button size="sm" variant="danger" className="w-full" disabled={busy} onClick={() => void abandon()}>
          {busy ? "Verifico sul gateway…" : `Conferma: abbandona #${pair.seq}`}
        </Button>
        <Button size="sm" variant="ghost" className="w-full" disabled={busy} onClick={() => setConfirm(false)}>
          Annulla
        </Button>
        {error && (
          <p role="alert" className="px-1 text-xs text-loss">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <button
      role="menuitem"
      type="button"
      onClick={() => setConfirm(true)}
      className="flex w-full items-center justify-between gap-2 rounded px-3 py-1.5 text-left text-sm hover:bg-raised"
    >
      <span>Abbandona</span>
      <Hint label="Quando si usa «Abbandona»">
        Solo se il gateway non risponde e la coppia resta appesa. Non chiude niente a mercato: dichiara che non sappiamo
        dove sia finita, e le posizioni potrebbero essere ancora a mercato.
      </Hint>
    </button>
  );
}

/**
 * Modifica dei livelli su una coppia a mercato.
 *
 * Si scrive DENARO, come in apertura, e nella valuta del conto prop: e' la
 * valuta in cui e' definito il valore punto, quindi la divisione torna senza
 * passare da un cambio. I prezzi li ricalcola il backend dal prezzo di apertura
 * EFFETTIVO di ciascuna gamba — non dalla quotazione corrente, o il rischio
 * promesso scivolerebbe col mercato.
 */
/**
 * La lettura MISURATA, non solo `LevelsState`: porta con sé `driftMoney`, cioè
 * lo scarto già convertito in valuta prop. Prendere qui il tipo più povero
 * voleva dire rimettersi a convertire nel browser.
 */
type MeasuredLevels = Extract<LevelsReading, { measured: true }>;

/** Sopra i 1.400px i due grafici stanno affiancati; sotto torna il selettore Prop | Broker. */
const WIDE_CHARTS_QUERY = "(min-width: 1400px)";

/**
 * Il denaro scritto nei campi SL/TP della coppia a mercato: unica fonte sia
 * per il grafico sopra le schede (che lo legge per la tratteggiata e lo
 * scrive trascinando) sia per il pannello sotto (che lo espone come campo).
 * Vive sopra `PairTabs`, perché il grafico non deve smontarsi passando da una
 * scheda all'altra — sono i predefiniti CHIESTI sull'ultima modifica, che la
 * coppia registra.
 */
function useLevelsInput(pair: HedgePair | null) {
  const [sl, setSl] = useState(pair?.slMoney ? String(Number(pair.slMoney)) : "");
  const [tp, setTp] = useState(pair?.tpMoney ? String(Number(pair.tpMoney)) : "");
  // Una coppia nuova (id diverso) riparte dai suoi predefiniti, non da quelli
  // lasciati dalla coppia precedente: senza questo, il campo di una coppia
  // appena aperta mostrerebbe ancora i numeri di quella appena chiusa.
  useEffect(() => {
    setSl(pair?.slMoney ? String(Number(pair.slMoney)) : "");
    setTp(pair?.tpMoney ? String(Number(pair.tpMoney)) : "");
  }, [pair?.id]);
  // Il trascinamento SCRIVE nel campo: l'input resta la fonte della decisione.
  const onDrag = useCallback((level: "SL" | "TP", valore: number) => {
    const testo = String(valore);
    if (level === "SL") setSl(testo);
    else setTp(testo);
  }, []);
  /*
   * «TOCCATO»: il campo dice altro da quello che la coppia ha registrato con
   * l'ultima apertura o modifica. Solo allora il grafico disegna l'anteprima
   * «chiesto»; dopo «Applica» la coppia registra il valore e la riga sparisce.
   * Vuoto e zero sono la stessa cosa (nessun livello).
   */
  const slEditing = decimalOrZero(sl) !== Number(pair?.slMoney ?? 0);
  const tpEditing = decimalOrZero(tp) !== Number(pair?.tpMoney ?? 0);
  return { sl, setSl, tp, setTp, onDrag, slEditing, tpEditing };
}

/**
 * I DUE GRAFICI, SOPRA LE SCHEDE (vincolo del 23/09): restano a schermo
 * passando da "Operazione di trading" a "Cronistoria", come il grafico di
 * MetaTrader che non si smonta cambiando pannello sotto. Per questo vivono
 * fuori da `PairTabs`, non dentro la sua scheda "trading".
 */
/** I passi del grafico, in minuti: M1 e M5, per i due grafici insieme. */
const CHART_TIMEFRAMES = [1, 5] as const;
type ChartTimeframe = (typeof CHART_TIMEFRAMES)[number];

/**
 * Lo storico che si vuole vedere: 24 ore. Sono 288 candele M5 e 1.440 M1, e il
 * tetto del driver (`MIRROR_MAX_BARS`) e' 1.440 proprio per questo. Quante ne
 * arrivano davvero lo dice la risposta (`barsRequested`/`barsReturned`): se il
 * gateway ne manda meno, il grafico e' piu' corto e la riga sopra lo scrive.
 */
const CHART_HISTORY_MINUTES = 24 * 60;

/** Le candele che coprono le 24 ore su quel passo. */
const barsFor = (tf: ChartTimeframe) => Math.ceil(CHART_HISTORY_MINUTES / tf);

/** «8 h 20 min» da un numero di minuti. */
const hoursMinutes = (min: number) => `${Math.floor(min / 60)} h${min % 60 ? ` ${Math.round(min % 60)} min` : ""}`;

function LevelsCharts({
  s,
  pair,
  symbol,
  canWrite,
  sl,
  tp,
  slEditing,
  tpEditing,
  onDrag,
  planned,
  plannedNote,
  onPlannedDrop,
}: {
  s: SessionDetail;
  /** La coppia a mercato, o `null` prima di aprire: allora solo le candele di `symbol`. */
  pair: HedgePair | null;
  /** Lo strumento (canonico) scelto nel form: vale solo senza coppia, con la coppia e' il suo. */
  symbol: string;
  canWrite: boolean;
  sl: string;
  tp: string;
  slEditing: boolean;
  tpEditing: boolean;
  onDrag: (level: "SL" | "TP", money: number) => void;
  /** Il piano dell'anteprima, prima di aprire: da qui le linee tratteggiate. `null` = nessuna linea. */
  planned: { prop: LegPlan; broker: LegPlan } | null;
  /** Perche' le linee dell'anteprima non ci sono, quando non e' solo un campo vuoto. */
  plannedNote: string | null;
  /** Una linea del piano lasciata sul grafico prop: il prezzo va all'anteprima. */
  onPlannedDrop: (level: "SL" | "TP", price: number) => Promise<void>;
}) {
  /** Sotto i 1.400px: quale gamba mostra il grafico singolo. Predefinito Prop, come prima. */
  const [chartRole, setChartRole] = useState<"PROP" | "BROKER">("PROP");
  /** Uno solo per tutt'e due i grafici: le due gambe si guardano sullo stesso passo. */
  const [timeframe, setTimeframe] = useState<ChartTimeframe>(5);
  const wide = useMediaQuery(WIDE_CHARTS_QUERY);

  const propConnected = s.propAccount.isConnected;
  const brokerConnected = s.brokerAccount?.isConnected ?? false;

  /**
   * UNA lettura per gamba e per passo, all'apertura della schermata (vincolo del
   * passo 11): non a ogni render, non a ogni trascinamento. Si leggono sempre
   * tutt'e due — anche sotto i 1.400px, dove ne compare uno solo — cosi'
   * passare da Prop a Broker col selettore non aspetta una seconda chiamata.
   *
   * **Solo a conto connesso.** Il backend, col conto spento, lo accenderebbe per
   * il tempo della lettura: qui invece si dice «connetti il conto» e si lascia
   * decidere a chi guarda.
   */
  const propCandles = useQuery({
    queryKey: [...keys.sessions, "candles", s.id, pair?.id ?? `aperta:${symbol}`, "PROP", timeframe],
    queryFn: () => api.sessions.candles(s.id, pair?.id ?? null, { role: "PROP", timeframe, bars: barsFor(timeframe), canonicalSymbol: symbol }),
    enabled: propConnected,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const brokerCandles = useQuery({
    queryKey: [...keys.sessions, "candles", s.id, pair?.id ?? `aperta:${symbol}`, "BROKER", timeframe],
    queryFn: () => api.sessions.candles(s.id, pair?.id ?? null, { role: "BROKER", timeframe, bars: barsFor(timeframe), canonicalSymbol: symbol }),
    enabled: brokerConnected,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const propLeg = pair?.legs.find((l) => l.accountRole === "PROP");
  const brokerLeg = pair?.legs.find((l) => l.accountRole === "BROKER");

  const pane = (role: "PROP" | "BROKER") => (
    <ChartPane
      role={role}
      q={role === "PROP" ? propCandles : brokerCandles}
      leg={role === "PROP" ? propLeg : brokerLeg}
      withPair={!!pair}
      account={role === "PROP" ? s.propAccount : s.brokerAccount}
      s={s}
      sl={sl}
      tp={tp}
      slEditing={slEditing}
      tpEditing={tpEditing}
      onDrag={onDrag}
      canWrite={canWrite}
      // Il ruolo prima del livello: ogni gamba ha i SUOI SL e TP, sul broker incrociati rispetto al prop.
      planned={pair ? null : ((role === "PROP" ? planned?.prop : planned?.broker) ?? null)}
      plannedNote={pair ? null : plannedNote}
      onPlannedDrop={onPlannedDrop}
    />
  );

  // Quello che si vede davvero, MISURATO sulle candele arrivate: se il gateway ne manda meno, lo si dice.
  const letto = propCandles.data ?? brokerCandles.data;
  const coperti = letto && letto.bars.length > 1 ? (Date.parse(letto.bars[letto.bars.length - 1]!.time) - Date.parse(letto.bars[0]!.time)) / 60_000 : null;
  const troncato = !!letto && letto.barsReturned < letto.barsRequested;
  const aggiorna = () => {
    void propCandles.refetch();
    void brokerCandles.refetch();
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div role="radiogroup" aria-label="Passo delle candele" className="inline-flex overflow-hidden rounded border border-line text-xs">
          {CHART_TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              type="button"
              role="radio"
              aria-checked={timeframe === tf}
              onClick={() => setTimeframe(tf)}
              className={`px-3 py-1 ${timeframe === tf ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
            >
              M{tf}
            </button>
          ))}
        </div>
        {!wide && (
          <div role="radiogroup" aria-label="Gamba del grafico" className="inline-flex overflow-hidden rounded border border-line text-xs">
            {(["PROP", "BROKER"] as const).map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={chartRole === r}
                onClick={() => setChartRole(r)}
                className={`px-3 py-1 ${chartRole === r ? (r === "PROP" ? "bg-prop/15 font-medium text-prop" : "bg-broker/15 font-medium text-broker") : "text-muted hover:text-ink"}`}
              >
                {isBrokerPair(s) ? BB_LEG_LABELS[r] : r === "PROP" ? "Prop" : "Broker"}
              </button>
            ))}
          </div>
        )}
        <Button size="sm" variant="ghost" disabled={propCandles.isFetching || brokerCandles.isFetching} onClick={aggiorna}>
          Aggiorna
        </Button>
        {troncato ? (
          <span className="text-xs text-warn" role="status">
            Il gateway ha mandato {letto.barsReturned.toLocaleString("it-IT")} candele M{timeframe} su {letto.barsRequested.toLocaleString("it-IT")} chieste
            {coperti !== null ? `: il grafico copre ${hoursMinutes(coperti)}, non 24 h` : ""}
          </span>
        ) : (
          <span className="text-xs text-faint">
            24 h chieste ({barsFor(timeframe).toLocaleString("it-IT")} candele M{timeframe})
            {letto && coperti !== null ? ` · lette ${letto.barsReturned.toLocaleString("it-IT")}, coprono ${hoursMinutes(coperti)}` : ""}
          </span>
        )}
      </div>
      {wide ? (
        <div className="grid gap-4 md:grid-cols-2">
          {pane("PROP")}
          {pane("BROKER")}
        </div>
      ) : (
        pane(chartRole)
      )}
    </div>
  );
}

function LevelsPanel({
  s,
  pair,
  levels,
  sl,
  setSl,
  tp,
  setTp,
}: {
  s: SessionDetail;
  pair: HedgePair;
  levels: MeasuredLevels | null;
  sl: string;
  setSl: (v: string) => void;
  tp: string;
  setTp: (v: string) => void;
}) {
  const refresh = useRefreshDirectory();
  const propCur = s.propAccount.currency;
  // I nomi delle due gambe: sulla Fase broker contro broker sono il broker A e il broker B.
  const nA = isBrokerPair(s) ? "broker A" : "prop";
  const nB = isBrokerPair(s) ? "broker B" : "broker";
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Il `code` del rifiuto: «non riuscita» e «non si è letto il valore punto» chiedono cose diverse. */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  const d = levels?.desync ?? null;
  /**
   * Quanto vale lo scarto, IN VALUTA PROP — la stessa della soglia.
   *
   * `desync.amount` nasce nella valuta della gamba sopravvissuta: con prop in
   * USD e broker in EUR, mostrarlo così accanto a una soglia in dollari
   * metterebbe due unità diverse nella stessa frase. La conversione la fa il
   * backend (`driftMoney`), che è anche quello che decide il blocco.
   */
  const soldi = levels?.driftMoney ?? null;
  /**
   * Lo scarto basta a fermare un'apertura nuova?
   *
   * La stessa regola del backend (`driftBlocksNewPair`): un importo che non si
   * sa — una gamba senza quel livello, o un cambio mancante — blocca comunque,
   * perché «non lo so» non è «zero».
   */
  const blocca = !!soldi && (soldi.amount === null || Math.abs(soldi.amount) >= s.levelsDriftLimit);

  /** «Riallinea»: la gamba broker sullo specchio del prop. Il prop non si tocca. */
  const riallinea = async () => {
    setBusy("riallinea");
    setError(null);
    setErrorCode(null);
    setEsito(null);
    try {
      const r = await api.sessions.realign(s.id, pair.id);
      setEsito(
        r.changed
          ? r.levels.desync
            ? "Riallineamento fatto, ma le gambe non si coprono ancora: guarda lo scarto rimasto."
            : `Gamba ${nB} riallineata: le due gambe si coprono di nuovo.`
          : "Le gambe erano già allineate: nessuna chiamata al gateway.",
      );
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  const invia = (nome: string, body: { slMoney: number; tpMoney?: number }) => async () => {
    setBusy(nome);
    setError(null);
    setErrorCode(null);
    setEsito(null);
    try {
      const r = await api.sessions.modifyLevels(s.id, pair.id, body);
      setEsito(
        r.changed
          ? `Livelli a mercato: rischio ${money(r.money.atMarket.sl.prop, r.money.propCurrency)} sul ${nA}, ` +
            `${money(r.money.atMarket.sl.broker, r.money.brokerCurrency)} sul ${nB}.`
          : "I livelli erano gia' questi: nessuna chiamata al gateway.",
      );
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  const valori = { sl: decimalInput(sl), tp: decimalInput(tp) };
  const corpo = () => ({ slMoney: Number(valori.sl), ...(valori.tp ? { tpMoney: Number(valori.tp) } : {}) });

  return (
    <section className="space-y-3 rounded-card border border-line bg-surface p-4">
      {d && (
        /*
         * LO SCARTO FRA LE GAMBE, in denaro, IN UNA RIGA.
         *
         * Dal 22/09 i livelli non si correggono piu' da soli dopo l'apertura: la
         * gamba prop tiene quelli mandati con l'ordine, e un po' di scarto lo
         * lascia lo slippage — e' la norma, quindi non urla. Il colore c'e' solo
         * sopra la soglia, perche' da li' blocca le aperture nuove. Le
         * spiegazioni stanno nell'Hint, non in un paragrafo.
         */
        <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className={blocca ? "font-medium text-loss" : "text-muted"}>
            Livelli non allineati: scoperto per{" "}
            {soldi?.amount != null
              ? money(soldi.amount, soldi.currency)
              : soldi?.fxMissing
                ? `un importo non calcolabile (manca il cambio ${soldi.raw.currency}/${soldi.currency})`
                : "un livello che una gamba non ha"}{" "}
            sulla gamba {d.survivingLeg === "PROP" ? nA : nB}
          </span>
          <Button size="sm" variant={blocca ? "danger" : "secondary"} disabled={!!busy} onClick={riallinea}>
            {busy === "riallinea" ? "Riallineamento…" : `Riallinea la gamba ${nB}`}
          </Button>
          <Hint label="Perché i livelli non coincidono">
            {d.gapPoints === null ? "Una gamba non ha quel livello. " : `${points(d.gapPoints)} di differenza sul ${d.level === "SL" ? "stop loss" : "take profit"}. `}
            {soldi?.converted && soldi.raw.amount !== null ? `Sul conto ${nB} sono ${money(soldi.raw.amount, soldi.raw.currency)}. ` : ""}
            {blocca
              ? `Soglia ${money(s.levelsDriftLimit, s.levelsDriftCurrency)}: finché resta così, su questa fase non si apre niente — il rischio scoperto si sommerebbe a quello nuovo. `
              : `Sotto la soglia di ${money(s.levelsDriftLimit, s.levelsDriftCurrency)}: non blocca le aperture. Lo lascia il riempimento, che sui due conti non slitta allo stesso modo. `}
            «Riallinea» porta la gamba {nB} sullo specchio del {nA}, misurata dal suo prezzo d'ingresso: la gamba {nA} non si tocca, ed è il motivo per cui la correzione automatica è stata tolta.
          </Hint>
        </div>
      )}
      {levels && !levels.desync && !levels.matchesRequest && (
        <p role="status" className="text-sm text-warn">
          Le due gambe si coprono, ma i livelli a mercato non sono quelli chiesti: l'ultima modifica non e' stata applicata.
        </p>
      )}

      <h3 className="text-sm font-medium text-ink">Modifica i livelli</h3>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <DecimalField label="Stop loss" value={sl} onChange={setSl} suffix={propCur} />
        <DecimalField label="Take profit" value={tp} onChange={setTp} suffix={propCur} hint="Vuoto = nessun take profit." />
        <div className="flex items-end">
          <Button disabled={!!busy || !valori.sl || parseDecimal(tp).kind === "error"} onClick={invia("modifica", corpo())}>
            {busy === "modifica" ? "Modifica…" : "Applica"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-faint">
        Le distanze si contano dal prezzo di apertura di ciascuna gamba, non dalla quotazione di adesso: il rischio che il
        gestionale promette e' la distanza dall'ingresso.
      </p>
      {errorCode === "VALUE_PER_POINT_UNREADABLE" ? <ValueUnreadableNote action="modifica" message={error ?? ""} /> : (esito ? <p className="text-sm text-muted">{esito}</p> : <ErrorNote>{error}</ErrorNote>)}
    </section>
  );
}

/**
 * Un grafico della coppia sulla gamba `role`. Il grafico e' la LETTURA: se non
 * c'e', i campi in denaro funzionano uguale — e' lo stesso comportamento del
 * trascinamento che non si puo' usare sul broker.
 */
function ChartPane({
  role,
  q,
  leg,
  withPair,
  account,
  s,
  sl,
  tp,
  slEditing,
  tpEditing,
  onDrag,
  canWrite,
  planned,
  plannedNote,
  onPlannedDrop,
}: {
  role: "PROP" | "BROKER";
  /** Con una coppia a mercato il grafico ha le sue linee; senza, sono le sole candele. */
  withPair: boolean;
  q: { data?: Candles; isError: boolean; isLoading?: boolean; error: unknown };
  leg: HedgeLeg | undefined;
  /** Il conto di questa gamba: dice se il grafico si puo' leggere, o se prima va connesso. */
  account: SessionDetail["propAccount"] | null | undefined;
  s: SessionDetail;
  sl: string;
  tp: string;
  slEditing: boolean;
  tpEditing: boolean;
  onDrag: (level: "SL" | "TP", money: number) => void;
  /** Il trascinamento resta un'esclusiva di chi scrive: chi legge soltanto vede il grafico, senza poterlo muovere. */
  canWrite: boolean;
  /** La gamba di QUESTO grafico nel piano dell'anteprima: prezzi e importi nella sua valuta. */
  planned: LegPlan | null;
  plannedNote: string | null;
  onPlannedDrop: (level: "SL" | "TP", price: number) => Promise<void>;
}) {
  const cur = role === "BROKER" ? (s.brokerAccount?.currency ?? s.propAccount.currency) : s.propAccount.currency;
  const chart = q.data;
  const label = isBrokerPair(s) ? (role === "PROP" ? "broker A" : "broker B") : role === "PROP" ? "prop" : "broker";

  // Conto spento: niente candele (il grafico non lo accende di nascosto), e il gesto per averle e' qui.
  if (!account?.isConnected) return <ConnectToSeeChart role={role} label={label} accountId={account?.id} canWrite={canWrite} />;

  return (
    <div className="space-y-2">
      {q.isLoading && <p className="text-xs text-faint">Caricamento del grafico {label}…</p>}
      {q.isError && (
        <p className="text-xs text-faint">
          Grafico {label} non disponibile: {errorMessage(q.error)}. I livelli si scrivono lo stesso nei campi qui sotto.
        </p>
      )}
      {chart && (!withPair || leg?.openPrice) && (
        <Suspense fallback={<p className="text-xs text-faint">Caricamento del grafico…</p>}>
          <LevelsChart
            candles={chart}
            leg={
              withPair && leg?.openPrice
                ? {
                    side: leg.side,
                    openPrice: Number(leg.openPrice),
                    slPrice: leg.slPrice === null ? null : Number(leg.slPrice),
                    tpPrice: leg.tpPrice === null ? null : Number(leg.tpPrice),
                    lots: Number(leg.lots),
                  }
                : null
            }
            point={chart.point}
            valuePerPoint={chart.valuePerPoint.value}
            currency={cur}
            slMoney={sl}
            tpMoney={tp}
            slEditing={slEditing}
            tpEditing={tpEditing}
            draggable={role === "PROP" && canWrite}
            onDrag={onDrag}
            planned={
              // Zero dal motore = livello assente (TP vuoto, e sul broker lo SL che gli corrisponde).
              planned
                ? {
                    side: planned.side,
                    entryPrice: planned.entryPrice || null,
                    slPrice: planned.slPrice || null,
                    tpPrice: planned.tpPrice || null,
                    slMoney: Math.abs(planned.lossAtSl),
                    tpMoney: Math.abs(planned.profitAtTp),
                  }
                : null
            }
            plannedNote={plannedNote}
            // Solo il Prop si trascina (`draggable`): il Broker si aggiorna dall'anteprima ricalcolata.
            onPlannedDrop={onPlannedDrop}
          />
        </Suspense>
      )}
      {chart && (
        <p className="text-xs text-faint">
          valore punto {money(chart.valuePerPoint.value, cur)} · <ValueSourceTag v={chart.valuePerPoint} />
          {withPair && role === "PROP" && " · da qui escono gli importi della scheda sotto"}
        </p>
      )}
    </div>
  );
}

/**
 * Al posto del grafico di un conto spento. Non e' un errore e non e' un
 * riquadro vuoto: dice che cosa manca e da' il pulsante. Chi puo' solo leggere
 * vede la frase, senza il pulsante.
 */
function ConnectToSeeChart({ role, label, accountId, canWrite }: { role: "PROP" | "BROKER"; label: string; accountId: string | undefined; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = async () => {
    if (!accountId) return;
    setBusy(true);
    setError(null);
    try {
      await api.accounts.connect(accountId);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  return (
    <div className={`flex min-h-[200px] flex-col items-center justify-center gap-3 rounded border border-dashed p-6 text-center ${role === "PROP" ? "border-prop/40" : "border-broker/40"}`}>
      <p className="text-sm text-muted">Connetti il conto {label} per vedere il grafico</p>
      {canWrite && accountId && (
        <Button size="sm" variant="primary" disabled={busy} onClick={() => void connect()}>
          {busy ? "Connessione…" : "Connetti"}
        </Button>
      )}
      {error && (
        <p role="alert" className="text-xs text-loss">
          {error}
        </p>
      )}
    </div>
  );
}

/** Chiusura delle due gambe, con conferma. Usata dalla coppia aperta e dall'elenco operazioni. */
export function ClosePairButton({ sessionId, pair, size }: { sessionId: string; pair: HedgePair; size?: "sm" }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.sessions.closePair(sessionId, pair.id);
      setConfirm(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {confirm ? (
        <>
          <Button size={size} variant="danger" disabled={busy} onClick={() => void close()}>
            {busy ? "Chiusura…" : `Conferma chiusura della coppia #${pair.seq}`}
          </Button>
          <Button size={size} variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
        </>
      ) : (
        <Button size={size} onClick={() => setConfirm(true)}>
          Chiudi coppia
        </Button>
      )}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}

/**
 * ABBANDONA LA COPPIA: l'uscita quando il gateway non sa piu' niente.
 *
 * Non e' una seconda «chiudi», ed e' scritto nel bottone: qui **non si chiude
 * niente a mercato**. Si dichiara che non sappiamo dove sia finita quella
 * coppia, cosi' la fase si sblocca e l'evento resta aperto finche' qualcuno non
 * e' andato a guardare dal terminale MT5.
 *
 * Il backend RIFIUTA se il gateway risponde — a mercato si chiude, sparita si
 * consolida — quindi il pulsante non deve nasconderlo: quei due rifiuti sono
 * informazione, e si mostrano come sono arrivati.
 */
export function AbandonPairButton({ sessionId, pair, size }: { sessionId: string; pair: HedgePair; size?: "sm" }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  const abandon = async () => {
    setBusy(true);
    setError(null);
    setEsito(null);
    try {
      const r = await api.sessions.abandonPair(sessionId, pair.id);
      setConfirm(false);
      setEsito(
        r.outcome === "CONSOLIDATED"
          ? "Il gateway ha risposto e non c'era piu' niente a mercato: la coppia e' stata consolidata, col suo P&L. Non e' stata abbandonata."
          : "Coppia abbandonata. L'evento resta aperto: controlla dal terminale MT5 che non ci siano posizioni ancora a mercato.",
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {confirm ? (
        <>
          <Button size={size} variant="danger" disabled={busy} onClick={() => void abandon()}>
            {busy ? "Verifico sul gateway…" : `Conferma: abbandona la coppia #${pair.seq}`}
          </Button>
          <Button size={size} variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
          <span className="text-xs text-faint">
            Non chiude niente a mercato: dichiara che non sappiamo dove sia finita.
          </span>
        </>
      ) : (
        <Button size={size} variant="ghost" onClick={() => setConfirm(true)}>
          Abbandona
        </Button>
      )}
      {esito && <span className="text-xs text-muted">{esito}</span>}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}
